import React, { useState, useEffect, useRef, useLayoutEffect, Suspense, lazy } from 'react';
import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation
} from 'react-router-dom';

import { Helmet } from 'react-helmet';

import {
  mount,
  unmount
} from 'redom';

import application from 'application';

import {
  isCommunityPath
} from 'utilities/routes';

import internalEventCodes from 'event-system/codes/internal';
import externalEventCodes from 'event-system/codes/external';

import useStartupTheme from 'generics/hooks/use-startup-theme';
import useOnClickOutside from 'generics/hooks/on-click-outside';

import Authorizer from 'root/authorizer';

import QuickAccess from './quick-access';
import NotFound from './not-found';
import HomePageRedirect from './home-page-redirect';
import MobileHeader from './mobile-header';
import Playground from './playground';
import GoogleCalendarIntegration from './google-calendar-integration';
import Assistant from './assistant/react';
import HelpDrawerWidget from './help-drawer';
import AssistantResizeHandle from './assistant-resize-handle';

import './index.scss';
import 'generics/components/right-panel-fullscreen-toggle/index.scss';

import { ReactComponent as RedPulsingDotIcon } from 'assets/images/red-pulsing-dot.svg';
import { ReactComponent as CloseIcon } from 'assets/images/close-button-icon.svg';

import StartupRouteWarmup from './startup-route-warmup';

import {
  Community,
  Customers,
  Pages,
  Dashboard,
  Inbox,
  Campaigns,
  Payments,
  Calendar,
  DataCollections,
  Products,
  Welcome,
  Skills
} from './startup-lazy-routes';

const defaultBannerText = 'Join our FREE bootcamp & LIVE office hours to build your venture and ask any questions 👉';

function StartupView(props) {

  const humanFriendlyId = props.humanFriendlyId;

  const navigate = useNavigate();
  const location = useLocation();

  const quickAccessDrawerReference = useRef(null);
  const helpDrawerContainerReference = useRef(null);
  const helpDrawerWidgetReference = useRef(null);
  const resizeHandleContainerReference = useRef(null);
  const assistantContainerReference = useRef(null);
  const resizeHandleReference = useRef(null);

  useOnClickOutside(quickAccessDrawerReference, closeQuickAccessDrawer);

  const [rootState, setRootState] = useState({
    authenticated: application.isAuthenticated(),
    banner: {
      text: defaultBannerText,
      link: 'https://meet.google.com/aiq-dphm-fdx'
    }
  });
  const [startup, setStartup] = useState(null);
  const [mobileAssistantVisible, setMobileAssistantVisible] = useState(false);

  useStartupTheme(startup, application.system.getFontFamilies());

  useEffect(function onLoad() {

    initialize();

    application.handleStartViewLoaded();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function registerForEvents() {

    const unlistenToStartupUpdates = listenToStartupUpdates();
    const unlistenStartupIconChangedEvent = listenToStartupIconChangedEvent();
    const unlistenToggleNavigationEvent = listenToToggleNavigationEvent();
    const unlistenEnableBodyScrollEvent = listenToEnableBodyScrollEvent();
    const unlistenHideNavigationEvent = listenToHideNavigationEvent();
    const unlistenPostDeletedEvent = listenToPostDeletedEvent();
    const unlistenToStartupLiveStreamingMinutesUpdatedEvent = listenToStartupLiveStreamingMinutesUpdatedEvent();
    const unlistenPreviewStartupThemeEvent = listenPreviewStartupThemeEvent();
    const unlistenUserLoggedOutEvent = listenUserLoggedOutEvent();

    return function unregisterEventListeners() {
      unlistenToStartupUpdates();
      unlistenStartupIconChangedEvent();
      unlistenToggleNavigationEvent();
      unlistenEnableBodyScrollEvent();
      unlistenHideNavigationEvent();
      unlistenPostDeletedEvent();
      unlistenToStartupLiveStreamingMinutesUpdatedEvent();
      unlistenPreviewStartupThemeEvent();
      unlistenUserLoggedOutEvent();
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(function onStartupChange() {

    if (startup && startup.humanFriendlyId === humanFriendlyId) {
      return;
    }

    activateStartup();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [humanFriendlyId]);

  useLayoutEffect(function onStartupChange() {

    if (startup === null) {
      return;
    }

    const helpDrawerContainer = helpDrawerContainerReference.current;

    if (helpDrawerWidgetReference.current === null && helpDrawerContainer !== null) {
      helpDrawerWidgetReference.current = new HelpDrawerWidget();
      mount(helpDrawerContainer, helpDrawerWidgetReference.current);
    }

    return function onStartupChangeUnload() {

      if (helpDrawerWidgetReference.current === null) {
        return;
      }

      unmount(helpDrawerContainer, helpDrawerWidgetReference.current);

    };

  }, [startup]);

  useLayoutEffect(function mountResizeHandle() {

    if (startup === null) {
      return;
    }

    const handleContainer = resizeHandleContainerReference.current;
    const assistantContainer = assistantContainerReference.current;

    if (resizeHandleReference.current !== null || handleContainer === null || assistantContainer === null) {
      return;
    }

    const handle = new AssistantResizeHandle({
      onClick() {
        return application.assistantManager.assistant.toggle();
      }
    });
    handle.setAssistantContainer(assistantContainer);
    mount(handleContainer, handle);
    resizeHandleReference.current = handle;

    application.assistantManager.initialize();

  }, [startup]);

  function tryNavigateToCommunity() {

    if (isCommunityPath(location.pathname) === true) {
      return;
    }

    navigate('/community');

  }

  function listenToPostDeletedEvent() {

    function handlePostDeletedEvent(post) {

      setStartup(function updateStartup(startup) {

        if (startup.communities[0].pinnedPostId !== post._id) {
          return startup;
        }

        return {
          ...startup,
          communities: [{
            ...startup.communities[0],
            pinnedPostId: null
          }]
        };

      });

    }

    application.eventSystem.internal.on(internalEventCodes.POST_DELETED, handlePostDeletedEvent);

    return function unlistenPostDeletedEvent() {
      application.eventSystem.internal.off(internalEventCodes.POST_DELETED, handlePostDeletedEvent);
    };

  }

  function listenToStartupIconChangedEvent() {

    function handleStartupIconChangedEvent(data) {

      setStartup(function updateStartup(startup) {

        return {
          ...startup,
          icon: data.icon
        };

      });

    }

    application.eventSystem.internal.on(internalEventCodes.STARTUP_ICON_CHANGED, handleStartupIconChangedEvent);

    return function unlistenStartupIconChangedEvent() {

      application.eventSystem.internal.off(internalEventCodes.STARTUP_ICON_CHANGED, handleStartupIconChangedEvent);

    };

  }

  function listenToToggleNavigationEvent() {

    function handleToggleNavigationEvent() {

      toggleNavigation();

    }

    application.eventSystem.internal.on(internalEventCodes.TOGGLE_NAVIGATION, handleToggleNavigationEvent);

    return function unlistenToggleNavigationEvent() {

      application.eventSystem.internal.off(internalEventCodes.TOGGLE_NAVIGATION, handleToggleNavigationEvent);

    };

  }

  function listenToHideNavigationEvent() {

    function handleHideNavigationEvent() {

      HideNavigation();

    }

    application.eventSystem.internal.on(internalEventCodes.TOGGLE_NAVIGATION, handleHideNavigationEvent);

    return function unlistenHideNavigationEvent() {

      application.eventSystem.internal.off(internalEventCodes.TOGGLE_NAVIGATION, handleHideNavigationEvent);

    };

  }

  function listenToEnableBodyScrollEvent() {

    function handleEnableBodyScrollEvent() {

      enableBodyScroll();

    }

    application.eventSystem.internal.on(internalEventCodes.ENABLE_BODY_SCROLL, handleEnableBodyScrollEvent);

    return function unlistenEnableBodyScrollEvent() {

      application.eventSystem.internal.off(internalEventCodes.ENABLE_BODY_SCROLL, handleEnableBodyScrollEvent);

    };

  }

  function listenToStartupUpdates() {

    function handleStartupSubdomainDeployedEvent(data) {
      setStartup(function updateStartup(startup) {

        if (data._id !== startup._id) {
          return startup;
        }

        if (data.domain.active === true) {
          return startup;
        }

        window.location.href = data.address.url;

      });
    }

    application.eventSystem.internal.on(internalEventCodes.STARTUP_SUBDOMAIN_DEPLOYED, handleStartupSubdomainDeployedEvent);

    return function unlistenToStartupUpdates() {

      application.eventSystem.internal.off(internalEventCodes.STARTUP_SUBDOMAIN_DEPLOYED, handleStartupSubdomainDeployedEvent);

    };

  }

  function listenToStartupLiveStreamingMinutesUpdatedEvent() {

    function handleStartupLiveStreamingMinutesUpdatedEvent(data) {

      setStartup(function updateStartup(startup) {

        if (data._id !== startup._id) {
          return startup;
        }

        application.updateCurrentStartup(data);

        return {
          ...startup,
          ...data
        };
      });

    }

    application.eventSystem.internal.on(externalEventCodes.STARTUP_LIVE_STREAMING_MINUTES_UDPATED, handleStartupLiveStreamingMinutesUpdatedEvent);

    return function unlistenStartupLiveStreamingMinutesUpdatedEvent() {
      application.eventSystem.internal.off(externalEventCodes.STARTUP_LIVE_STREAMING_MINUTES_UDPATED, handleStartupLiveStreamingMinutesUpdatedEvent);
    };

  }

  function listenPreviewStartupThemeEvent() {

    function handlePreviewStartupThemeEvent(theme) {

      setStartup(function updateStartup(startup) {

        return {
          ...startup,
          theme
        };

      });

    }

    application.eventSystem.internal.on(internalEventCodes.PREVIEW_STARTUP_THEME, handlePreviewStartupThemeEvent);

    return function unlistenStartupLiveStreamingMinutesUpdatedEvent() {
      application.eventSystem.internal.off(internalEventCodes.PREVIEW_STARTUP_THEME, handlePreviewStartupThemeEvent);
    };

  }

  function listenUserLoggedOutEvent() {

    function handleUserLoggedOutEvent() {

      setRootState(function updateRootState(rootState) {

        return {
          ...rootState,
          authenticated: false
        };

      });

      tryNavigateToCommunity();

    }

    application.eventSystem.internal.on(internalEventCodes.USER_LOGGED_OUT, handleUserLoggedOutEvent);

    return function unlistenStartupLiveStreamingMinutesUpdatedEvent() {
      application.eventSystem.internal.off(internalEventCodes.USER_LOGGED_OUT, handleUserLoggedOutEvent);
    };

  }

  async function initialize() {

    await activateStartup();
    application.eventSystem.internal.emit(internalEventCodes.HIDE_NAVIGATION);

  }

  function setBanner(banner) {

    setRootState(function updateRootState(rootState) {

      return {
        ...rootState,
        banner
      };

    });

  }

  async function activateStartup(reload) {

    application.setCurrentStartupPathPrefix(getPathPrefix());

    const startup = application.getCurrentStartup();

    setBanner(application.banner);
    setStartup(startup);

    trySetFirstTimeLoading(startup);

  }

  function updateCommunity(community) {

    setStartup(function updateStartup(startup) {

      return {
        ...startup,
        communities: [{
          ...startup.communities[0],
          ...community
        }]
      };

    });

  }

  async function trySetFirstTimeLoading(startup) {

    if (rootState.authenticated === false) {
      return;
    }

    if (startup.ownerId !== application.getUser()._id) {
      return;
    }

    if (startup.loadingFirstTime === false) {
      return;
    }

    const updated = await application.serverInterface.startup.updateStartupLoadingFirstTimeById(startup._id);

    if (updated === true) {

      await application.platformUserObserver.emitStartupCreatedEvent({
        name: startup.name
      });

    }

  }

  function getPathPrefix() {

    const _pathPrefix = props.patternPrefix.replace(':humanFriendlyId', humanFriendlyId);

    if (_pathPrefix === '/') {
      return '';
    }

    return _pathPrefix;

  }

  function HideNavigation() {

    enableBodyScroll();

  }

  function closeQuickAccessDrawer() {

    const element = quickAccessDrawerReference.current;

    if (element === null) {
      return;
    }

    element.style.width = 0;
    element.style.padding = 0;

  }

  function toggleNavigation() {

    const className = 'navigation-on';

    if (document.body.classList.contains(className) === true) {
      return document.body.classList.remove(className);
    }

    return document.body.classList.add(className);

  }

  function enableBodyScroll() {

    const className = 'navigation-on';

    if (document.body.classList.contains(className) === false) {
      return;
    }

    document.body.classList.remove(className);

  }

  function determineStaticContentContainerClass() {

    if (application.isAuthenticated() === true) {
      if (application.accessController.isMemberOrAdministrator() === true) {
        const owner = startup.owner;
        if (owner.invoiceOverDue === true || owner.metricLimitReached === true) {
          return 'coverall';
        }
      }

    }

    return '';

  }

  function determineUserLevelClass() {

    if (application.isAuthenticated() === false) {
      return 'visitor';
    }

    if (application.accessController.isMemberOrAdministrator() === false) {
      return 'customer';
    }

    return 'member';

  }

  function canRenderStartupContent() {

    if (startup === null) {
      return false;
    }

    if (application.isAuthenticated() === true) {
      if (application.accessController.isMemberOrAdministrator() === true) {
        const owner = startup.owner;
        if (owner.invoiceOverDue === true || owner.metricLimitReached === true) {
          return false;
        }
      }

    }

    return true;

  }

  function canRenderBanner() {

    if (rootState.banner === null) {
      return false;
    }

    if (application.isAuthenticated() === false) {
      return false;
    }

    if (application.accessController.isCustomer() === true) {
      return false;
    }

    return true;

  }

  function renderLoadingSpinner() {
    return <div className="spinner-container">
      <ht-spinner size="small"></ht-spinner>
    </div>;
  }

  function renderSpinner() {

    if (startup !== null) {
      return;
    }

    return renderLoadingSpinner();

  }

  function renderWelcomeRoute() {

    const authorizerProperties = {
      if () {
        return rootState.authenticated && application.accessController.isMemberOrAdministrator();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const welcomeViewProperties = {
      startup,
      assistant: true
    };

    const routeProperties = {
      path: 'welcome',
      element: <Authorizer name="Welcome" {...authorizerProperties}>
        <Welcome {...welcomeViewProperties} />
      </Authorizer>
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderRedirectRoute(path) {

    const properties = {
      path: '',
      element: <Navigate to={path} />
    };

    return <Route {...properties}>

    </Route>;

  }

  function renderDefaultRoute() {

    let path = '/community';

    if (startup !== null && rootState.authenticated && application.getUser()._id === startup.ownerId) {
      path = '/welcome';
    }

    return <React.Fragment>
      {renderRedirectRoute(path)}
    </React.Fragment>;

  }

  function renderCustomersRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {

      },
      else(navigate) {
        navigate('/community');
      }
    };

    const routeProperties = {
      path: 'contacts/*',
      element: <Authorizer name="Customers" {...authorizerProperties}>
        <Customers />
      </Authorizer>
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderInboxRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const inboxRouteProperties = {
      path: 'inbox',
      element: <Authorizer name="Inbox" {...authorizerProperties}>
        <Inbox {...properties} />
      </Authorizer>
    };

    const conversationRouteProperties = {
      path: 'inbox/conversations/:conversationId',
      element: <Authorizer name="Inbox" {...authorizerProperties}>
        <Inbox {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...inboxRouteProperties}></Route>
      <Route {...conversationRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderPaymentsRouteDefinition() {

    if (application.isPaymentsModuleEnabled() === false) {
      return;
    }

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const paymentsRouteProperties = {
      path: 'finance',
      element: <Authorizer name="Payments" {...authorizerProperties}>
        <Payments {...properties} />
      </Authorizer>
    };

    const paymentDetailsRouteProperties = {
      path: 'finance/:paymentId',
      element: <Authorizer name="Payments" {...authorizerProperties}>
        <Payments {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...paymentsRouteProperties}></Route>
      <Route {...paymentDetailsRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderCalendarRouteDefinition() {

    if (application.isCalendarModuleEnabled() === false) {
      return;
    }

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const calendarRouteProperties = {
      path: 'tasks',
      element: <Authorizer name="Calendar" {...authorizerProperties}>
        <Calendar {...properties} />
      </Authorizer>
    };

    const calendarDetailsRouteProperties = {
      path: 'tasks/:activityId',
      element: <Authorizer name="Calendar" {...authorizerProperties}>
        <Calendar {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...calendarRouteProperties}></Route>
      <Route {...calendarDetailsRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderDataCollectionsRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const dataCollectionsRouteProperties = {
      path: 'databases',
      element: <Authorizer name="DataCollections" {...authorizerProperties}>
        <DataCollections {...properties} />
      </Authorizer>
    };

    const dataCollectionsDetailsRouteProperties = {
      path: 'databases/:databaseId',
      element: <Authorizer name="DataCollections" {...authorizerProperties}>
        <DataCollections {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...dataCollectionsRouteProperties}></Route>
      <Route {...dataCollectionsDetailsRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderProductsRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const productsRouteProperties = {
      path: 'products',
      element: <Authorizer name="Products" {...authorizerProperties}>
        <Products {...properties} />
      </Authorizer>
    };

    const productsDetailsRouteProperties = {
      path: 'products/:productId',
      element: <Authorizer name="Products" {...authorizerProperties}>
        <Products {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...productsRouteProperties}></Route>
      <Route {...productsDetailsRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderSkillsRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const properties = { startup };

    const skillsRouteProperties = {
      path: 'skills',
      element: <Authorizer name="Skills" {...authorizerProperties}>
        <Skills {...properties} />
      </Authorizer>
    };

    const skillsDetailsRouteProperties = {
      path: 'skills/:skillId',
      element: <Authorizer name="Skills" {...authorizerProperties}>
        <Skills {...properties} />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...skillsRouteProperties}></Route>
      <Route {...skillsDetailsRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderCampaignsRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const campaignsRouteProperties = {
      path: 'campaigns',
      element: <Authorizer name="Campaigns" {...authorizerProperties}>
        <Campaigns />
      </Authorizer>
    };

    const campaignRouteProperties = {
      path: 'campaigns/:campaignId',
      element: <Authorizer name="Campaigns" {...authorizerProperties}>
        <Campaigns />
      </Authorizer>
    };

    return <React.Fragment>
      <Route {...campaignsRouteProperties}></Route>
      <Route {...campaignRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderPagesRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.isAuthenticated();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const pageProperties = { startup };
    const pageRouteProperties = {
      path: 'pages/*',
      element: <Authorizer name="Campaigns" {...authorizerProperties}>
        <Pages {...pageProperties} />
      </Authorizer>
    };

    return <Route {...pageRouteProperties}></Route>;
  }

  function renderDashboardRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.accessController.isAllowedToViewCustomersSection();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const pageProperties = { startup };
    const routeProperties = {
      path: 'analytics',
      element: <Authorizer name="Dashboard" {...authorizerProperties}>
        <Dashboard {...pageProperties} />
      </Authorizer>
    };
    const reportRouteProperties = {
      ...routeProperties,
      path: 'analytics/:reportId'
    };

    return <React.Fragment>
      <Route {...routeProperties}></Route>
      <Route {...reportRouteProperties}></Route>
    </React.Fragment>;

  }

  function renderDisabledCommunityRouteDefinition() {

    let element = <HomePageRedirect />;

    if (rootState.authenticated === true && application.accessController.isMemberOrAdministrator() === true) {
      element = <Navigate to={application.getCommunityFallbackPath()} replace />;
    }

    const routeProperties = {
      path: 'community/*',
      element
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderCommunityRouteDefinition() {

    if (application.isCommunityEnabled() === false) {
      return renderDisabledCommunityRouteDefinition();
    }

    const communityProperties = {
      startup,
      community: startup.communities[0],
      updateCommunity
    };

    const routeProperties = {
      path: 'community/*',
      element: <Community {...communityProperties} />
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderPlaygroundRouteDefinition() {

    const properties = {
      startup,
      community: startup.communities[0],
      updateCommunity
    };

    const routeProperties = {
      path: 'playground/*',
      element: <Playground {...properties} />
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderGoogleCalendarIntegrationRouteDefinition() {

    const properties = {
      startup
    };

    const routeProperties = {
      path: '/google-calendar-authorization/callback',
      element: <GoogleCalendarIntegration {...properties} />
    };

    return <Route {...routeProperties}></Route>;

  }

  function renderAssistantRouteDefinition() {

    const authorizerProperties = {
      if () {
        return application.isAuthenticated();
      },
      then(navigate, urlParameters) {},
      else(navigate) {
        navigate('/community');
      }
    };

    const pageProperties = { startup };
    const pageRouteProperties = {
      path: 'assistant/*',
      element: <Authorizer name="Assistant" {...authorizerProperties}>
        <Assistant {...pageProperties} />
      </Authorizer>
    };

    return <Route {...pageRouteProperties}></Route>;
  }

  function renderCatchOthers() {

    return <Route path="*" element={<NotFound />} />;

  }

  function renderRoutes() {

    return <Routes>
      {renderWelcomeRoute()}
      {renderDashboardRouteDefinition()}
      {renderPagesRouteDefinition()}
      {renderCommunityRouteDefinition()}
      {renderCustomersRouteDefinition()}
      {renderCampaignsRouteDefinition()}
      {renderInboxRouteDefinition()}
      {renderPaymentsRouteDefinition()}
      {renderCalendarRouteDefinition()}
      {renderDataCollectionsRouteDefinition()}
      {renderProductsRouteDefinition()}
      {renderSkillsRouteDefinition()}
      {renderPlaygroundRouteDefinition()}
      {renderGoogleCalendarIntegrationRouteDefinition()}
      {renderAssistantRouteDefinition()}
      {renderDefaultRoute()}
      {renderCatchOthers()}
    </Routes>;

  }

  function renderStartupViewContent() {

    if (canRenderStartupContent() === false) {
      return;
    }

    return <React.Fragment>
      {renderMetaTags()}
      {renderRoutes()}
    </React.Fragment>;

  }

  function renderMetaTags() {

    const description = `Home of ${startup.name}`;

    return <Helmet>
      <title>{startup.name}</title>
      <meta name="description" content={description} />
    </Helmet>;

  }

  function renderQuickAccess(onOptionClick = function noop() {}) {

    if (startup == null) {
      return;
    }

    const quickAccessProps = {
      startup,
      activateStartup,
      onOptionClick
    };

    return <QuickAccess {...quickAccessProps} />;

  }

  function tryRenderQuickAccess() {

    return renderQuickAccess();

  }

  function openMobileAssistant() {
    setMobileAssistantVisible(true);
  }

  function closeMobileAssistant() {
    setMobileAssistantVisible(false);
  }

  function canRenderAssistant() {

    if (application.isAuthenticated() === false) {
      return false;
    }

    return application.accessController.isMemberOrAdministrator();

  }

  function tryRenderStartupMainStaticMobileHeader() {

    if (application.domUtility.isMobile() === false) {
      return;
    }

    const mobileHeaderProperties = {
      startup,
      activateStartup,
      assistantAvailable: canRenderAssistant(),
      onAssistantOpen: openMobileAssistant
    };

    return <MobileHeader {...mobileHeaderProperties} />;

  }

  function tryRenderHelpDrawer() {

    if (canRenderStartupContent() === false) {
      return;
    }

    return <div id="startup-help-drawer"></div>;

  }

  function tryRenderBanner() {

    if (canRenderBanner() === false) {
      return;
    }

    return <div id="banner">
      <div id="banner-content">
        <RedPulsingDotIcon />
        <a href={decodeURIComponent(rootState.banner.link)} target="_blank" rel="noreferrer">{rootState.banner.text || defaultBannerText}</a>
      </div>
    </div>;

  }

  function tryRenderAssistantContainers() {

    if (canRenderAssistant() === false) {
      return;
    }

    const overlayClassName = mobileAssistantVisible ? 'open' : '';

    return <React.Fragment>
      <div id="assistant-resize-handle-container" ref={resizeHandleContainerReference}></div>
      <div id="assistant-mobile-overlay-container" className={overlayClassName}>
        <button className="assistant-mobile-close-button" onClick={closeMobileAssistant}>
          <CloseIcon />
        </button>
        <div id="assistant-container" ref={assistantContainerReference}></div>
      </div>
    </React.Fragment>;

  }

  function renderNewLayout() {

    if (startup === null) {
      return renderSpinner();
    }

    const staticContentContainerProperties = {
      id: 'startup-main-static-content-container',
      className: `${determineStaticContentContainerClass()} ${determineUserLevelClass()}`
    };

    return <div id="startup-main">

      {tryRenderStartupMainStaticMobileHeader()}
      {tryRenderBanner()}

      <div {...staticContentContainerProperties}>

        <div id="quick-access-container">
          {tryRenderQuickAccess()}
        </div>

        <div id="content-container">
          <Suspense fallback={renderLoadingSpinner()}>
            {renderStartupViewContent()}
          </Suspense>
          <StartupRouteWarmup
            startup={startup}
            authenticated={rootState.authenticated}
            pathname={location.pathname}
            pathPrefix={getPathPrefix()}
          />
          {tryRenderAssistantContainers()}
        </div>

        {tryRenderHelpDrawer()}

        <div className="cover"></div>

      </div>

      <div id="banners-container">

      </div>
      <div id="dialogs-container">
      </div>

      <div id="startup-view-extras-container">

      </div>

      <div id="out-of-place-content-container">
      </div>

    </div>;

  }

  return renderNewLayout();

}

export default StartupView;
